import React, { useMemo } from 'react';
import { Box, Stack, Tab, Tabs, Typography } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import { hide, IConciergeEditorContext } from 'store/conciergeSlice';
import type { ITestSession } from 'api/concierge';
import { useDispatch, useSelector } from 'react-redux';
import IconHandler from 'components/common/icon/IconHandler';

import { mergeCustomStyles } from 'app/concierge/tools/mergeCustomStyles';
import { ICustomStyles } from 'app/concierge/panel';
import defaultStyles from './styles';

export default function Header({
    activeTab,
    setActiveTab,
    setLastInteraction,
    botIcon,
    openedChatTitle = null,
}: {
    activeTab: string;
    setActiveTab: (tab: string) => void;
    setLastInteraction: (date: number) => void;
    botIcon?: string;
    /** A chat reopened from Recent Chats: the Concierge tab carries its title on both tabs. */
    openedChatTitle?: string | null;
}) {
    const { t } = useBundleTranslation(['app/concierge/widget']);
    const dispatch = useDispatch();
    const conciergeSettings = useSelector((state: any) => state.auth?.configuration?.conciergeSettings) ?? {};
    const customStyles = useSelector((state: any) => state.concierge.customStyles) as ICustomStyles;
    // The model whose editor is open (MI-30532): named so the steward knows why an answer came from
    // it. A test session carries its own banner, so the line steps aside for it.
    const editorContext = useSelector((state: any) => state.concierge.editorContext as IConciergeEditorContext | null);
    const testSession = useSelector((state: any) => state.concierge.testSession as ITestSession | null);

    const styles = useMemo(() => {
        return mergeCustomStyles(defaultStyles, customStyles?.header, 'container');
    }, [customStyles]);

    const conciergeIconProps = {
        type: botIcon ? 'upload' : conciergeSettings.bot_icon_ind,
        value: botIcon
            ? botIcon
            : conciergeSettings.bot_icon_ind === 'upload'
              ? `data:image/png;base64,${conciergeSettings.bot_icon_data}`
              : conciergeSettings.bot_icon_name,
    };

    // A reopened chat's title stays on the Concierge tab while Recent Chats is open too.
    const showChatTitle = null !== openedChatTitle;
    const tabs = [
        {
            key: 'concierge',
            icon: conciergeIconProps,
            label: showChatTitle ? openedChatTitle : t('header.tabs.concierge'),
        },
        { key: 'recent', icon: { type: 'mi', value: 'history' }, label: t('header.tabs.recent') },
    ];

    return (
        <Stack sx={styles.container}>
            <Stack direction={'row'} sx={styles.row}>
                <Box sx={styles.tabs}>
                    <Tabs
                        value={activeTab}
                        onChange={(event: React.SyntheticEvent, newValue: string) => {
                            setActiveTab(newValue);
                        }}
                        sx={{ mb: '-1px' }}
                    >
                        {tabs.map((tab) => {
                            return (
                                <Tab
                                    label={
                                        <Box
                                            component="span"
                                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                        >
                                            {tab.label}
                                        </Box>
                                    }
                                    title={
                                        showChatTitle && 'concierge' === tab.key ? (tab.label ?? undefined) : undefined
                                    }
                                    key={tab.key}
                                    value={tab.key}
                                    data-test={`concierge_header_tab_${tab.key}`}
                                    sx={'concierge' === tab.key ? styles.chatTab : styles.recentTab}
                                    icon={<IconHandler icon={tab.icon} fontSize={'16'} />}
                                    iconPosition="start"
                                />
                            );
                        })}
                    </Tabs>
                </Box>
                {/* A plain element on purpose: app custom_styles may restyle every .MuiBox-root / .MuiStack-root in
                    the header (position included), which would unpin the button from the right edge. */}
                <div style={styles.closeWrap}>
                    <Box
                        data-test="concierge_header_close_button"
                        onClick={() => {
                            setLastInteraction(Date.now());
                            dispatch(hide());
                        }}
                        sx={styles.closeBtn}
                    >
                        <IconMi icon="times" sx={{ fontSize: '16px' }} />
                    </Box>
                </div>
            </Stack>
            {editorContext && !testSession && (
                <Typography data-test="concierge_header_answering_on" sx={styles.context}>
                    {t('header.answering_on', { model: editorContext.modelName })}
                </Typography>
            )}
        </Stack>
    );
}
